import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

interface SubscriptionStatus {
  isSubscribed: boolean;
  isActive: boolean;
  expiresAt: string | null;
  loading: boolean;
  status?: string;
}

export function useSubscriptionStatus(calendarId: string, userId?: string): SubscriptionStatus {
  const [status, setStatus] = useState<SubscriptionStatus>({
    isSubscribed: false,
    isActive: false,
    expiresAt: null,
    loading: true
  });

  useEffect(() => {
    if (!userId) {
      setStatus(prev => ({ ...prev, loading: false }));
      return;
    }

    const checkSubscriptionStatus = async () => {
      try {
        // Check both subscriptions and event tickets
        const [subscriptionResponse, ticketResponse] = await Promise.all([
          supabase
            .from('subscriptions')
            .select('status, current_period_end')
            .eq('calendar_id', calendarId)
            .eq('user_id', userId)
            .maybeSingle(),
          supabase
            .from('event_tickets')
            .select('status')
            .eq('calendar_id', calendarId)
            .eq('user_id', userId)
            .eq('status', 'paid')
            .maybeSingle()
        ]);

        if (subscriptionResponse.error && ticketResponse.error) {
          throw subscriptionResponse.error;
        }

        const subscription = subscriptionResponse.data;
        const ticket = ticketResponse.data;

        // User has access if they have either a valid subscription or a paid ticket
        const hasAccess = (subscription && (
          subscription.status === 'active' || 
          (subscription.status === 'canceled' && new Date(subscription.current_period_end) > new Date())
        )) || (ticket && ticket.status === 'paid');

        setStatus({
          isSubscribed: hasAccess,
          isActive: hasAccess,
          expiresAt: subscription?.current_period_end || null,
          status: subscription?.status,
          loading: false
        });
      } catch (err) {
        console.error('Error checking subscription status:', err);
        setStatus(prev => ({ ...prev, loading: false }));
      }
    };

    // Initial check
    checkSubscriptionStatus();

    // Subscribe to changes
    const subscription = supabase
      .channel('subscription-status')
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `calendar_id=eq.${calendarId},user_id=eq.${userId}`
        },
        () => {
          checkSubscriptionStatus();
        }
      )
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'event_tickets',
          filter: `calendar_id=eq.${calendarId},user_id=eq.${userId}`
        },
        () => {
          checkSubscriptionStatus();
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [calendarId, userId]);

  return status;
}